import { create } from 'zustand';
import type { FileTreeNode } from '../types/wizard';
import type { TerminalMessage, SSEEvent, ProjectOption } from '../lib/tryit-types';
import { createSession, streamChat, getProjects } from '../lib/tryit-api';

type Screen = 'project-select' | 'workspace';

interface TryItState {
  // Screen
  screen: Screen;

  // Project selection
  projects: ProjectOption[];
  selectedProjectId: string | null;
  bedrockAvailable: boolean;

  // Session
  sessionId: string | null;
  messagesRemaining: number;
  isLoading: boolean;
  error: string | null;

  // Workspace
  projectName: string;
  files: Record<string, string>;
  fileTree: FileTreeNode[];

  // Editor
  openTabs: string[];
  activeTab: string | null;

  // Explorer
  selectedPath: string | null;
  explorerVisible: boolean;

  // Terminal
  messages: TerminalMessage[];
  isStreaming: boolean;
  hasStarted: boolean;

  // Actions
  loadProjects: () => Promise<void>;
  selectProject: (projectId: string) => Promise<void>;
  backToProjectSelect: () => void;
  openFile: (path: string) => void;
  closeTab: (path: string) => void;
  selectTab: (path: string) => void;
  toggleExplorer: () => void;
  sendMessage: (message: string) => void;
  newSession: () => void;
}

let idCounter = 0;
function nextId(): string {
  return `msg-${++idCounter}-${Date.now()}`;
}

export const useTryItStore = create<TryItState>((set, get) => ({
  // Screen
  screen: 'project-select',

  // Project selection
  projects: [],
  selectedProjectId: null,
  bedrockAvailable: false,

  // Session
  sessionId: null,
  messagesRemaining: 10,
  isLoading: false,
  error: null,

  // Workspace
  projectName: '',
  files: {},
  fileTree: [],

  // Editor
  openTabs: [],
  activeTab: null,

  // Explorer
  selectedPath: null,
  explorerVisible: true,

  // Terminal
  messages: [],
  isStreaming: false,
  hasStarted: false,

  // Actions
  loadProjects: async () => {
    set({ isLoading: true, error: null });
    try {
      const data = await getProjects();
      set({
        projects: data.projects,
        bedrockAvailable: data.bedrockAvailable,
        isLoading: false,
      });
    } catch (err) {
      set({
        isLoading: false,
        error: err instanceof Error ? err.message : 'Failed to load projects',
      });
    }
  },

  selectProject: async (projectId: string) => {
    set({ isLoading: true, error: null, selectedProjectId: projectId });
    try {
      const session = await createSession(projectId);
      set({
        screen: 'workspace',
        sessionId: session.sessionId,
        bedrockAvailable: session.bedrockAvailable,
        messagesRemaining: session.limits.messagesRemaining,
        projectName: session.workspace.projectName,
        files: session.workspace.files,
        fileTree: session.workspace.fileTree,
        isLoading: false,
        openTabs: ['claude/CLAUDE.md'],
        activeTab: 'claude/CLAUDE.md',
        selectedPath: 'claude/CLAUDE.md',
        hasStarted: false,
        messages: [
          {
            id: nextId(),
            type: 'system',
            content: `Connected to ${session.workspace.projectName} workspace. ${
              session.bedrockAvailable
                ? 'Claude Code is ready — try an assignment or type a question.'
                : 'Bedrock is not configured. Set AWS credentials to enable Claude.'
            }`,
            timestamp: Date.now(),
          },
        ],
      });
    } catch (err) {
      set({
        isLoading: false,
        error: err instanceof Error ? err.message : 'Failed to create session',
      });
    }
  },

  backToProjectSelect: () => {
    set({
      screen: 'project-select',
      sessionId: null,
      messages: [],
      openTabs: [],
      activeTab: null,
      hasStarted: false,
      isStreaming: false,
      selectedProjectId: null,
      error: null,
    });
  },

  openFile: (path: string) => {
    const { files, openTabs } = get();
    if (!files[path]) return;
    const tabs = openTabs.includes(path) ? openTabs : [...openTabs, path];
    set({ openTabs: tabs, activeTab: path, selectedPath: path });
  },

  closeTab: (path: string) => {
    const { openTabs, activeTab } = get();
    const tabs = openTabs.filter((t) => t !== path);
    let newActive = activeTab;
    if (activeTab === path) {
      const idx = openTabs.indexOf(path);
      newActive = tabs[Math.min(idx, tabs.length - 1)] ?? null;
    }
    set({ openTabs: tabs, activeTab: newActive });
  },

  selectTab: (path: string) => {
    set({ activeTab: path, selectedPath: path });
  },

  toggleExplorer: () => {
    set({ explorerVisible: !get().explorerVisible });
  },

  newSession: () => {
    const { selectedProjectId } = get();
    set({
      sessionId: null,
      messages: [],
      openTabs: [],
      activeTab: null,
      hasStarted: false,
      isStreaming: false,
    });
    if (selectedProjectId) {
      get().selectProject(selectedProjectId);
    }
  },

  sendMessage: (message: string) => {
    const { sessionId, isStreaming, bedrockAvailable, messagesRemaining } =
      get();
    if (!sessionId || isStreaming) return;

    if (!bedrockAvailable) {
      set({
        messages: [
          ...get().messages,
          {
            id: nextId(),
            type: 'user',
            content: message,
            timestamp: Date.now(),
          },
          {
            id: nextId(),
            type: 'error',
            content:
              'Bedrock is not configured. Set AWS_ACCESS_KEY_ID and AWS_SECRET_ACCESS_KEY to enable Claude.',
            timestamp: Date.now(),
          },
        ],
        hasStarted: true,
      });
      return;
    }

    if (messagesRemaining <= 0) {
      set({
        messages: [
          ...get().messages,
          {
            id: nextId(),
            type: 'error',
            content:
              'Session message limit reached. Start a new session to continue.',
            timestamp: Date.now(),
          },
        ],
      });
      return;
    }

    // Add user message
    set({
      messages: [
        ...get().messages,
        {
          id: nextId(),
          type: 'user',
          content: message,
          timestamp: Date.now(),
        },
      ],
      isStreaming: true,
      hasStarted: true,
    });

    // Track the current assistant text accumulator
    let assistantTextId: string | null = null;
    let accumulatedText = '';

    streamChat(
      sessionId,
      message,
      (event: SSEEvent) => {
        const state = get();
        switch (event.type) {
          case 'text': {
            if (!assistantTextId) {
              assistantTextId = nextId();
              accumulatedText = event.content;
              set({
                messages: [
                  ...state.messages,
                  {
                    id: assistantTextId,
                    type: 'assistant_text',
                    content: accumulatedText,
                    timestamp: Date.now(),
                  },
                ],
              });
            } else {
              accumulatedText += event.content;
              set({
                messages: state.messages.map((m) =>
                  m.id === assistantTextId
                    ? { ...m, content: accumulatedText }
                    : m,
                ),
              });
            }
            break;
          }
          case 'tool_use':
            // Reset text accumulator for next text block
            assistantTextId = null;
            accumulatedText = '';
            set({
              messages: [
                ...state.messages,
                {
                  id: nextId(),
                  type: 'tool_use',
                  content: `Using tool: ${event.tool}`,
                  toolName: event.tool,
                  toolInput: event.input,
                  toolId: event.id,
                  timestamp: Date.now(),
                },
              ],
            });
            break;
          case 'tool_result':
            set({
              messages: [
                ...state.messages,
                {
                  id: nextId(),
                  type: 'tool_result',
                  content: event.output,
                  toolName: event.tool,
                  toolId: event.id,
                  timestamp: Date.now(),
                },
              ],
            });
            break;
          case 'rate_limit':
            set({ messagesRemaining: event.messagesRemaining });
            break;
          case 'error':
            set({
              messages: [
                ...state.messages,
                {
                  id: nextId(),
                  type: 'error',
                  content: event.content,
                  timestamp: Date.now(),
                },
              ],
            });
            break;
          case 'complete':
            break;
        }
      },
      (error) => {
        set({
          isStreaming: false,
          messages: [
            ...get().messages,
            {
              id: nextId(),
              type: 'error',
              content: error.message,
              timestamp: Date.now(),
            },
          ],
        });
      },
      () => {
        set({ isStreaming: false });
      },
    );
  },
}));
